import {Fetcher} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {DesignGraphic} from '@mintdata-wsp/common/state/ui/visualDesigner/assets/DesignAssetsState';
import {GraphicSource, GraphicType, IGraphic, IGraphicFilters} from '@mintdata/mint-kit/api/assets/Graphic';
import {IGraphicProps} from '@mintdata/mint-kit/api/assets/IAssetsProps';
import {Icon} from '@mintdata/mint-kit/api/assets/Icon';
import {Illustration1} from '@mintdata/mint-kit/api/assets/Illustration1';
import {Illustration2} from '@mintdata/mint-kit/api/assets/Illustration2';
import {UploadedImage} from '@mintdata/mint-kit/api/assets/UploadedImage';
import {IPromiseCancelable, PromiseUtil} from '@mintdata/mint-kit/impl/util/PromiseUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class GraphicsController
{
    private _fetcher:Fetcher;

    public constructor()
    {
        this._fetcher = new Fetcher();
    }

    public getProps():IGraphicProps
    {
        return {
            searchGraphics: this.searchGraphics,
            getGraphic: this.getGraphic,
            getUploadedImageByUrl: commonControllers.assets.uploadedImages.getUploadedImageByUrl,
            addGraphicToFavorites: this.addGraphicToFavorites,
            removeGraphicFromFavorites: this.removeGraphicFromFavorites,
            addGraphicToRecent: this.addGraphicToRecent,
            isGraphicFavorite: this.isGraphicFavorite
        };
    }

    @bind
    public searchGraphics(text:string, filters?:IGraphicFilters):IPromiseCancelable<IGraphic[]>
    {
        return PromiseUtil.makeCancelable(this._searchGraphics(text, filters));
    }

    public getGraphic(type:GraphicType, name:string):IPromiseCancelable<IGraphic>
    {
        switch( type )
        {
            case GraphicType.Icon:
                return commonControllers.assets.icons.getIcon(name);

            case GraphicType.Illustration1:
                return commonControllers.assets.illustrations1.getIllustration(name);

            case GraphicType.Illustration2:
                return commonControllers.assets.illustrations2.getIllustration(name);

            case GraphicType.UploadedImage:
                return commonControllers.assets.uploadedImages.getUploadedImage(name);
        }

        return PromiseUtil.makeCancelable(Promise.resolve(null));
    }

    @action.bound
    public addGraphicToFavorites(graphic:IGraphic):void
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( openDesign )
        {
            const {type, name} = graphic.getRef();
            const {graphicsState} = openDesign.designState.assets;

            const existingGraphic:DesignGraphic = graphicsState.favoriteGraphics.find((designGraphic:DesignGraphic) => (
                designGraphic.type === type && designGraphic.name === name
            ));

            if( !existingGraphic )
                graphicsState.favoriteGraphics.unshift(DesignGraphic.fromGraphic(graphic));
        }
    }

    @action.bound
    public removeGraphicFromFavorites(graphic:IGraphic):void
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( openDesign )
        {
            const {type, name} = graphic.getRef();
            const {graphicsState} = openDesign.designState.assets;

            const existingGraphic:DesignGraphic = graphicsState.favoriteGraphics.find((designGraphic:DesignGraphic) => (
                designGraphic.type === type && designGraphic.name === name
            ));

            if( existingGraphic )
            {
                (graphicsState.favoriteGraphics as any).remove(existingGraphic);
                graphicsState.recentGraphics.slice(0, 50);
            }
        }
    }

    @bind
    public isGraphicFavorite(graphic:IGraphic):boolean
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( openDesign )
        {
            const {type, name} = graphic.getRef();
            const {graphicsState} = openDesign.designState.assets;

            const existingGraphic:DesignGraphic = graphicsState.favoriteGraphics.find((designGraphic:DesignGraphic) => (
                designGraphic.type === type && designGraphic.name === name
            ));

            return !!existingGraphic;
        }

        return false;
    }

    @action.bound
    public addGraphicToRecent(graphic:IGraphic):void
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( openDesign )
        {
            const {type, name} = graphic.getRef();
            const {graphicsState} = openDesign.designState.assets;

            graphicsState.recentGraphics = graphicsState.recentGraphics.filter((designGraphic:DesignGraphic) => (
                !(designGraphic.type === type && designGraphic.name === name)
            ));

            graphicsState.recentGraphics.unshift(DesignGraphic.fromGraphic(graphic));
            graphicsState.recentGraphics.slice(0, 50);
        }
    }

    @bind
    private async _searchGraphics(text:string, filters?:IGraphicFilters):Promise<IGraphic[]>
    {
        const types:GraphicType[] = (filters && filters.types && filters.types.length > 0)
            ? filters.types
            : this._getAllGraphicTypes();

        const source:GraphicSource = filters ? filters.source : null;

        let sourceGraphics:IGraphic[] = null;

        if( source )
        {
            sourceGraphics = source === GraphicSource.Favorite
                ? await this._getFavoriteGraphics()
                : await this._getRecentGraphics();
        }

        const foundGraphics:IGraphic[][] = await Promise.all(types.map(async (type:GraphicType) =>
        {
            const graphicsOfType = sourceGraphics && sourceGraphics.filter((graphic:IGraphic) => graphic.getType() === type);

            switch( type )
            {
                case GraphicType.Icon:
                {
                    return await commonControllers.assets.icons.searchIcons(
                        text, filters.iconFilters, graphicsOfType as Icon[]
                    ).promise;
                }

                case GraphicType.Illustration1:
                    return await commonControllers.assets.illustrations1.searchIllustrations(
                        text, filters.illustration1Filters, graphicsOfType as Illustration1[]
                    ).promise;

                case GraphicType.Illustration2:
                {
                    return await commonControllers.assets.illustrations2.searchIllustrations(
                        text, graphicsOfType as Illustration2[]
                    ).promise;
                }

                case GraphicType.UploadedImage:
                {
                    return await commonControllers.assets.uploadedImages.searchUploadedImages(
                        text, graphicsOfType as UploadedImage[]
                    ).promise;
                }
            }

            return [];
        }));

        const result:IGraphic[] = [].concat(...foundGraphics);

        if( sourceGraphics )
            return this._sortFoundGraphics(sourceGraphics, result);

        return [].concat(...foundGraphics);
    }

    private _sortFoundGraphics(sourceGraphics:IGraphic[], foundGraphics:IGraphic[]):IGraphic[]
    {
        const sourceOrder:Map<string, number> = new Map();

        sourceGraphics.forEach((graphic, index) => sourceOrder.set(graphic.getRef().toString(), index));

        return foundGraphics.sort((graphic1, graphic2) => (
            sourceOrder.get(graphic1.getRef().toString()) - sourceOrder.get(graphic2.getRef().toString())
        ));
    }

    @bind
    private async _getFavoriteGraphics():Promise<IGraphic[]>
    {
        const result:IGraphic[] = [];

        const openDesign = commonControllers.designState.getOpenDesign();

        if( openDesign )
        {
            const favoriteGraphics:DesignGraphic[] = openDesign.designState.assets.graphicsState.favoriteGraphics;

            return await this._designGraphicsToGraphics(favoriteGraphics);
        }

        return result;
    }

    @bind
    private async _getRecentGraphics():Promise<IGraphic[]>
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( openDesign )
        {
            const recentGraphics:DesignGraphic[] = openDesign.designState.assets.graphicsState.recentGraphics;

            return await this._designGraphicsToGraphics(recentGraphics);
        }

        return [];
    }

    private async _designGraphicsToGraphics(designGraphics:DesignGraphic[]):Promise<IGraphic[]>
    {
        const result:IGraphic[] = [];

        for( const designGraphic of designGraphics )
        {
            switch( designGraphic.type )
            {
                case GraphicType.Icon:
                {
                    result.push(await commonControllers.assets.icons.getIcon(designGraphic.name).promise);
                    break;
                }

                case GraphicType.Illustration1:
                {
                    result.push(await commonControllers.assets.illustrations1.getIllustration(designGraphic.name).promise);
                    break;
                }

                case GraphicType.Illustration2:
                {
                    result.push(await commonControllers.assets.illustrations2.getIllustration(designGraphic.name).promise);
                    break;
                }

                case GraphicType.UploadedImage:
                {
                    result.push(await commonControllers.assets.uploadedImages.getUploadedImage(designGraphic.name).promise);
                    break;
                }
            }
        }

        return result;
    }

    private _getAllGraphicTypes():GraphicType[]
    {
        return [
            GraphicType.Icon,
            GraphicType.Illustration1,
            GraphicType.Illustration2,
            GraphicType.Photo,
            GraphicType.UploadedImage
        ];
    }
}
